import { normalizeSaleUnit } from './productSaleUnit';

export type PricingMode = 'manual' | 'markup';
export type BranchPaymentBucket = 'cash' | 'card' | 'transfer';

export interface BranchPaymentConfig {
  cash: string[];
  card: string[];
  transfer: string[];
}

export interface AppPreferences {
  pricingMode: PricingMode;
  defaultMarkupPercent: number;
  dailySummaryTime: string;
  enabledSaleUnits: Record<string, boolean>;
  purchaseCurrency: string;
  saleCurrency: string;
  quickDiscounts: number[];
  quickDiscountEnabled: boolean;
  quickDiscountOptional: boolean;
  branchPaymentMethods: Record<string, BranchPaymentConfig>;
  /** Chakana mijoz cashback — savdoning sof foydasidan foiz */
  retailCashbackPercent: number;
}

const KEY_LEGACY = 'adde_app_preferences_v1';

/** Laravel `SystemController::ALLOWED_SALE_UNITS` bilan bir xil — faqat shular yuboriladi. */
const OPERATIONAL_ENABLED_SALE_UNITS = [
  'piece',
  'kg',
  'meter',
  'liter',
  'box',
  'pack',
  'roll',
  'set',
  'pallet',
  'ton',
  'sqm',
  'cubic_meter',
  'cm',
  'mm',
  'pair',
  'size',
  'wholesale',
] as const;

const OPERATIONAL_CURRENCIES = new Set(['UZS', 'USD', 'EUR', 'RUB', 'KZT', 'TRY', 'CNY']);

function coerceOperationalBoolean(v: unknown, defaultVal: boolean): boolean {
  if (v === undefined || v === null) return defaultVal;
  if (typeof v === 'boolean') return v;
  if (typeof v === 'number') return v !== 0;
  const s = String(v).trim().toLowerCase();
  if (s === 'true' || s === '1' || s === 'yes') return true;
  if (s === 'false' || s === '0' || s === 'no' || s === '') return false;
  return defaultVal;
}

/** Eski bitta kalit (tenant bo‘lmagan yoki eski versiya). */
export const APP_PREFERENCES_STORAGE_KEY = KEY_LEGACY;

/** localStorage da shu prefiksdan boshlangan kalitlar tenant sozlamalari. */
export const APP_PREFERENCES_KEY_PREFIX = 'adde_app_preferences_v1';

/** setAppPreferences chaqirilganda (shu tabda ham) tinglovchilar yangilashi uchun */
export const APP_PREFERENCES_CHANGED_EVENT = 'adde-app-prefs-changed';

export const DEFAULT_APP_PREFERENCES: AppPreferences = {
  pricingMode: 'manual',
  defaultMarkupPercent: 30,
  dailySummaryTime: '21:00',
  enabledSaleUnits: {
    piece: true,
    kg: true,
    meter: true,
    liter: true,
    box: true,
    pack: true,
    roll: true,
    set: true,
    pallet: true,
    ton: true,
    sqm: true,
    cubic_meter: true,
    cm: true,
    mm: true,
    pair: true,
    size: true,
    wholesale: true,
  },
  purchaseCurrency: 'UZS',
  saleCurrency: 'UZS',
  quickDiscounts: [15, 30, 50, 75],
  quickDiscountEnabled: true,
  quickDiscountOptional: true,
  branchPaymentMethods: {},
  retailCashbackPercent: 0,
};

/** Laravel `date_format:H:i` — faqat `09:30` / `21:00` (sekundlarsiz, ikki xonali soat). */
function normalizeDailySummaryTimeForApi(raw: unknown): string {
  const s = String(raw ?? '').trim();
  const m = s.match(/^(\d{1,2}):(\d{2})/);
  if (!m) return DEFAULT_APP_PREFERENCES.dailySummaryTime;
  let h = parseInt(m[1], 10);
  let min = parseInt(m[2], 10);
  if (!Number.isFinite(h) || !Number.isFinite(min)) return DEFAULT_APP_PREFERENCES.dailySummaryTime;
  h = Math.max(0, Math.min(23, h));
  min = Math.max(0, Math.min(59, min));
  return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
}

/**
 * Har bir tenant uchun alohida kalit; `tenant_id` JWT da bo‘lmasa — foydalanuvchi ID (boshqa kompaniya
 * ma’lumotlari bilan bir xil brauzerda aralashmasin).
 */
export function getAppPreferencesStorageKey(tenantId?: string | null, userId?: string | null): string {
  const tid = tenantId != null && String(tenantId).trim() !== '' ? String(tenantId).trim() : '';
  if (tid) return `${KEY_LEGACY}_t_${tid}`;
  const uid = userId != null && String(userId).trim() !== '' ? String(userId).trim() : '';
  if (uid) return `${KEY_LEGACY}_u_${uid}`;
  return KEY_LEGACY;
}

function sanitizePaymentNames(list: unknown): string[] {
  if (!Array.isArray(list)) return [];
  const out: string[] = [];
  const seen = new Set<string>();
  for (const x of list) {
    const s = String(x ?? '').trim();
    if (!s) continue;
    const k = s.toLowerCase();
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(s);
  }
  return out.slice(0, 20);
}

function sanitizeBranchPaymentMethods(input: unknown): Record<string, BranchPaymentConfig> {
  if (!input || typeof input !== 'object' || Array.isArray(input)) return {};
  const src = input as Record<string, unknown>;
  const out: Record<string, BranchPaymentConfig> = {};
  Object.entries(src).forEach(([branchId, raw]) => {
    const nid = Number.parseInt(String(branchId ?? '').trim(), 10);
    if (!Number.isFinite(nid) || nid <= 0) return;
    const key = String(nid);
    const cfg = raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {};
    out[key] = {
      cash: sanitizePaymentNames(cfg.cash),
      card: sanitizePaymentNames(cfg.card),
      transfer: sanitizePaymentNames(cfg.transfer),
    };
  });
  return out;
}

/** PUT operational: faqat joriy tenant filiallari kalitlari (422 / aralashgan legacy kalitlardan himoya). */
export function pickBranchPaymentMethodsForBranchList(
  bpm: Record<string, BranchPaymentConfig> | undefined,
  branches: Array<{ id?: string | number | null }>,
): Record<string, BranchPaymentConfig> {
  const allowed = new Set<string>();
  for (const b of branches || []) {
    const nid = Number.parseInt(String(b?.id ?? '').trim(), 10);
    if (Number.isFinite(nid) && nid > 0) allowed.add(String(nid));
  }
  if (allowed.size === 0) return sanitizeBranchPaymentMethods(bpm);
  const src = bpm && typeof bpm === 'object' ? bpm : {};
  const out: Record<string, BranchPaymentConfig> = {};
  for (const [k, v] of Object.entries(src)) {
    const nid = Number.parseInt(String(k).trim(), 10);
    if (!Number.isFinite(nid) || nid <= 0) continue;
    const key = String(nid);
    if (!allowed.has(key)) continue;
    out[key] =
      v && typeof v === 'object' && !Array.isArray(v)
        ? {
            cash: sanitizePaymentNames((v as BranchPaymentConfig).cash),
            card: sanitizePaymentNames((v as BranchPaymentConfig).card),
            transfer: sanitizePaymentNames((v as BranchPaymentConfig).transfer),
          }
        : { cash: [], card: [], transfer: [] };
  }
  return out;
}

/**
 * Serverdan operatsion prefs yuklanganda: `branchPaymentMethods` bo‘sh `{}` bo‘lsa ham server manbasi
 * hisoblanadi — aks holda `Object.keys(remote).length > 0` sharti lokal defaultlarni cheksiz qoldirardi.
 * Muvaffaqiyatli javobda to‘lovlar faqat serverdan — lokalda qolgan boshqa-tenant / eski filial kalitlari aralashmaydi.
 */
export function mergeBranchPaymentMethodsFromOperational(
  local: Record<string, BranchPaymentConfig> | undefined,
  operationalPrefs: Partial<AppPreferences> | null | undefined,
): Record<string, BranchPaymentConfig> {
  const l = local && typeof local === 'object' && !Array.isArray(local) ? { ...local } : {};
  if (operationalPrefs == null) {
    return sanitizeBranchPaymentMethods(l);
  }
  const remote = operationalPrefs.branchPaymentMethods;
  if (remote == null || typeof remote !== 'object' || Array.isArray(remote)) {
    return sanitizeBranchPaymentMethods(l);
  }
  return sanitizeBranchPaymentMethods({ ...remote });
}

export function sanitizeAppPreferences(input: Partial<AppPreferences> | null | undefined): AppPreferences {
  const src = input || {};
  const rawUnits = (src.enabledSaleUnits || {}) as Record<string, unknown>;
  const allowedSet = new Set<string>(OPERATIONAL_ENABLED_SALE_UNITS as unknown as string[]);
  const normalizedUnits: Record<string, boolean> = {};
  Object.entries(rawUnits).forEach(([k, v]) => {
    const nk = normalizeSaleUnit(k);
    if (!allowedSet.has(nk)) return;
    const def = DEFAULT_APP_PREFERENCES.enabledSaleUnits[nk as keyof AppPreferences['enabledSaleUnits']];
    normalizedUnits[nk] = coerceOperationalBoolean(v, Boolean(def));
  });
  const enabledSaleUnits: Record<string, boolean> = {};
  for (const unit of OPERATIONAL_ENABLED_SALE_UNITS) {
    const def = DEFAULT_APP_PREFERENCES.enabledSaleUnits[unit as keyof AppPreferences['enabledSaleUnits']];
    enabledSaleUnits[unit] =
      normalizedUnits[unit] !== undefined ? normalizedUnits[unit] : Boolean(def);
  }
  const rawDiscounts = Array.isArray(src.quickDiscounts) ? src.quickDiscounts : DEFAULT_APP_PREFERENCES.quickDiscounts;
  const quickDiscounts = [...rawDiscounts]
    .map((x) => Math.max(0, Math.min(100, Number(x) || 0)))
    .slice(0, 4);
  while (quickDiscounts.length < 4) quickDiscounts.push(0);
  const markup = Math.max(0, Math.min(500, Number(src.defaultMarkupPercent ?? DEFAULT_APP_PREFERENCES.defaultMarkupPercent) || 0));
  return {
    pricingMode: src.pricingMode === 'markup' ? 'markup' : 'manual',
    defaultMarkupPercent: markup,
    dailySummaryTime: normalizeDailySummaryTimeForApi(src.dailySummaryTime ?? DEFAULT_APP_PREFERENCES.dailySummaryTime),
    enabledSaleUnits,
    purchaseCurrency: (() => {
      const c = String(src.purchaseCurrency ?? DEFAULT_APP_PREFERENCES.purchaseCurrency ?? 'UZS').toUpperCase();
      return OPERATIONAL_CURRENCIES.has(c) ? c : DEFAULT_APP_PREFERENCES.purchaseCurrency;
    })(),
    saleCurrency: (() => {
      const c = String(src.saleCurrency ?? DEFAULT_APP_PREFERENCES.saleCurrency ?? 'UZS').toUpperCase();
      return OPERATIONAL_CURRENCIES.has(c) ? c : DEFAULT_APP_PREFERENCES.saleCurrency;
    })(),
    quickDiscounts,
    quickDiscountEnabled: coerceOperationalBoolean(src.quickDiscountEnabled, DEFAULT_APP_PREFERENCES.quickDiscountEnabled),
    quickDiscountOptional: coerceOperationalBoolean(src.quickDiscountOptional, DEFAULT_APP_PREFERENCES.quickDiscountOptional),
    branchPaymentMethods: sanitizeBranchPaymentMethods(src.branchPaymentMethods),
    retailCashbackPercent: Math.max(0, Math.min(100, Number(src.retailCashbackPercent ?? DEFAULT_APP_PREFERENCES.retailCashbackPercent) || 0)),
  };
}

/**
 * @param tenantId — `user.tenantId`
 * @param userId — `tenantId` bo‘lmasa, shu foydalanuvchiga xos kalit (kross-tenant aralashmaslik).
 */
export function getAppPreferences(tenantId?: string | null, userId?: string | null): AppPreferences {
  try {
    const storageKey = getAppPreferencesStorageKey(tenantId, userId);
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return DEFAULT_APP_PREFERENCES;
    const parsed = JSON.parse(raw) as Partial<AppPreferences>;
    return sanitizeAppPreferences(parsed);
  } catch {
    return DEFAULT_APP_PREFERENCES;
  }
}

export function setAppPreferences(next: AppPreferences, tenantId?: string | null, userId?: string | null): void {
  const storageKey = getAppPreferencesStorageKey(tenantId, userId);
  window.localStorage.setItem(storageKey, JSON.stringify(next));
  try {
    window.dispatchEvent(new Event(APP_PREFERENCES_CHANGED_EVENT));
  } catch {
    /* ignore */
  }
}
